<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="modelValue"
        @mousedown.self="handleOutsideClick"
        class="flex items-center justify-center fixed w-full h-full text_color font-medium draggable_modal_wrapper"
        :class="{ 'pointer-events-none': !showOverlay }"
      >
        <Transition name="slide-fade">
          <div
            v-if="showOverlay"
            class="w-full h-full fixed draggable_modal_overlay"
          ></div>
        </Transition>
        <div
          class="draggable_modal rounded-lg pointer-events-auto"
          :style="[modalStyle, { width: `${width}px` }]"
          @mousedown.stop
        >
          <header
            :class="`draggable_modal_header flex flex-row justify-between items-center border_bottom ${headerPadding}`"
            @mousedown="startDrag"
          >
            <div class="flex flex-row items-center gap-2">
              <img
                v-if="icon"
                :class="`size-[${size}px]`"
                :src="icon"
                :alt="`${alt}_icon`"
              />
              <h2 class="text-sm">{{ title }}</h2>
            </div>
            <ThemeImage
              class="size-5 cursor-pointer"
              @click="closeModal"
              :icon="imageNames.CLOSE_GREY"
              alt="cross_icon"
            />
          </header>
          <slot></slot>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";

import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  title: { type: String, required: true },
  modelValue: { type: Boolean, required: true },
  showOverlay: { type: Boolean, default: true },
  width: { type: Number, default: 300 },
  icon: { type: String, required: false },
  size: { type: Number, default: 20 },
  alt: { type: String, default: "default" },
  headerPadding: { type: String, default: "p-4" },
  initialPosition: {
    type: Object,
    default: () => ({ x: 0, y: 0 }),
  },
});

const emit = defineEmits(["update:modelValue", "positionChange"]);

const position = ref({ x: 0, y: 0 });
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });

const modalStyle = computed(() => ({
  transform: `translate(${position.value.x}px, ${position.value.y}px)`,
  transition: isDragging.value ? "none" : "transform 0.3s ease",
}));

watch(
  () => props.initialPosition,
  (newPos) => {
    if (!isDragging.value) {
      position.value = { ...newPos };
    }
  },
  { immediate: true },
);

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      position.value = { ...props.initialPosition };
    }
  },
);

const closeModal = () => {
  emit("update:modelValue", false);
};

const handleOutsideClick = () => {
  if (props.showOverlay) {
    closeModal();
  }
};

const startDrag = (event) => {
  isDragging.value = true;
  dragStart.value = {
    x: event.clientX - position.value.x,
    y: event.clientY - position.value.y,
  };
  document.addEventListener("mousemove", drag);
  document.addEventListener("mouseup", stopDrag);
};

const drag = (event) => {
  if (isDragging.value) {
    const newPosition = {
      x: event.clientX - dragStart.value.x,
      y: event.clientY - dragStart.value.y,
    };
    position.value = newPosition;
    emit("positionChange", newPosition);
  }
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener("mousemove", drag);
  document.removeEventListener("mouseup", stopDrag);
};

onMounted(() => {
  document.addEventListener("mousemove", drag);
  document.addEventListener("mouseup", stopDrag);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousemove", drag);
  document.removeEventListener("mouseup", stopDrag);
});
</script>

<style scoped>
.draggable_modal_wrapper {
  top: 0;
  left: 0;
  z-index: 1000;
}

.draggable_modal_overlay {
  top: 0;
  left: 0;
  background-color: var(--black-opacity-50);
}

.draggable_modal {
  background-color: var(--draggable-modal-bg);
  box-shadow: 0 2px 8px var(--black-opacity-15);
  position: absolute;
  max-width: 1000px;
  user-select: none;
}

.draggable_modal_header {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  cursor: move;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}
</style>
